import { Component, computed, NgZone, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { Visitor } from '@app/common-codes/models/matwins-tenant/Visitor.model';
import { Employee } from '@app/common-codes/models/matwins-tenant/Employee.model';
import { DataService } from '@app/common-codes/services/DataService';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { VISITOR_TYPES, VisitorType } from '../../ipad-visitor-type/ipad-visitor-type.component';
import { CheckinBaseComponent } from '../checkin-base.component';
import { CheckinShellComponent } from '../checkin-shell/checkin-shell.component';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { QuizViewerComponent } from "../quiz/quiz-viewer.component";
import { PolicyViewerComponent } from "../policy/policy-viewer.component";
import { HealthyViewerComponent } from "../healthy/healthy-viewer.component";
import { EngagementViewerComponent } from "../contractor-checkin/engagement-viewer.component";
import { PhotoCaptureComponent } from "../../photo/photo-capture.component";
import { QrScannerComponent } from "../../qr-scanner/qr-scanner.component";

@Register(Visit)
@Component({
    selector: 'app-employee-checkin',
    templateUrl: './employee-checkin.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, OnReadyDirective, FieldWrapperComponent, MultiSelectComponent, CheckinShellComponent, PolicyViewerComponent, EngagementViewerComponent, HealthyViewerComponent, QuizViewerComponent, PhotoCaptureComponent, QrScannerComponent]
})
export class EmployeeCheckinComponent extends CheckinBaseComponent {

    visitorType: VisitorType | null = VISITOR_TYPES.find(vt => vt.key === 'employee') ?? null;

    employeeVisitorOptions = computed(() => {
        const visitors = this.visitorsLookup();
        // Tous les employés (pas seulement les managers) : tout employé peut
        // s'enregistrer — le scan QR peut résoudre n'importe lequel d'entre eux.
        const employees = this.rawEmployeesLookup();

        // Pas de déduplication par nom : un visiteur de l'historique peut porter
        // le même nom qu'un employé — les deux options doivent exister pour que
        // la clé 'emp_<id>' puisse se résoudre en label dans le multi-select.
        const options: LookupItem[] = visitors.map(v => ({
            ...v,
            additionalData: { ...v.additionalData, _lookupSource: 'visitor' }
        }));

        for (const e of employees) {
            const prefixedId = `emp_${e.id}`;
            options.push({
                id: prefixedId,
                key: prefixedId,
                value: e.value,
                sortOrder: e.sortOrder,
                visible: e.visible,
                additionalData: { ...e.additionalData, _lookupSource: 'employee', _originalId: e.id },
                children: e.children,
                selectedValue: e.selectedValue
            });
        }

        return options;
    });

    private employeeDataService!: DataService<Employee>;

    photoTaken = signal(false);
    qrScanned = signal(false);
    private qrScanProcessing = false;

    constructor(
        route: ActivatedRoute,
        router: Router,
        translationService: TranslationService,
        validationService: GenericValidationService,
        ngZone: NgZone,
        navService: NavigationService,
        tenantService: CurrentTenantService,
        alertModalService: AlertModalService
    ) {
        super(route, router, translationService, validationService, ngZone, navService, tenantService, alertModalService);
        this.employeeDataService = this.di.getOrCreateDataService(Employee);
    }

    /** Le scan QR est proposé si le POS l'active, après la photo quand celle-ci est requise. */
    showEmployeeQrScanner(): boolean {
        const pos = this.currentPos;
        if (!pos?.dS_NewEmployee_QrCode || this.qrScanned()) return false;
        return !pos.dS_NewEmployee_Camera || this.photoTaken();
    }

    onEmployeePhotoFile(file: File): void {
        this.onPhotoFile(file);
        this.photoTaken.set(true);
    }

    async onEmployeeQrScanned(code: string): Promise<void> {
        if (this.qrScanProcessing || this.qrScanned()) return;
        this.qrScanProcessing = true;
        this.loaderService.show();

        try {
            const posId = this.tenantService.currentPointOfServiceId() ?? 0;
            const employee = await this.employeeDataService.execute<Employee>(
                'GET', 'byQrCode', { code, posId }
            );

            if (employee?.id) {
                this.qrScanned.set(true);
                await this.applyScannedEmployee(employee);
                this.alertModalService.showToast(
                    this.translationService.instant('vues.ipad.scan.recognized'), ToastType.Success, 3000
                );
            } else {
                this.alertModalService.showToast(
                    this.translationService.instant('vues.ipad.scan.not-recognized'), ToastType.Error, 3000
                );
            }
        } catch {
            this.alertModalService.showToast(
                this.translationService.instant('vues.ipad.scan.not-recognized'), ToastType.Error, 3000
            );
        } finally {
            this.loaderService.hide();
            setTimeout(() => { this.qrScanProcessing = false; }, 1500);
        }
    }

    /**
     * Process du scan QR :
     * 1. L'employé est résolu via byQrCode.
     * 2. On cherche un visiteur déjà venu lié à cet employé
     *    (visites portant FK_Employee_Origin = employé) → s'il existe, on le
     *    réutilise via le process visiteur standard (getLastVisit, réutilisation
     *    signature/policies selon policiesFrequency, manager de la dernière visite).
     * 3. Sinon on crée le visiteur à partir des données employé et on pré-remplit
     *    le champ "employé" avec son manager.
     */
    protected async applyScannedEmployee(employee: Employee): Promise<void> {
        const visit = this.editingEntity();
        if (!visit || !employee?.id) return;

        const empId = String(employee.id);
        const returningVisitor = this.rawVisitorsLookup().find(v =>
            (v.additionalData?.['employeeoriginaggreg'] as string | undefined)
                ?.split(',')
                .some(t => t.trim() === empId)
        );

        if (returningVisitor) {
            await super.onVisitorSelectChange([returningVisitor.id]);
            const updated = this.editingEntity();
            if (updated) {
                updated.fK_Employee_Origin = employee.id;
                this.editingEntity.set(Object.assign(new Visit(), updated));
            }
            return;
        }

        this.applyEmployeeAsVisitor(employee);
    }

    /**
     * Crée le visiteur à partir d'un employé (scan QR ou sélection manuelle
     * 'emp_<id>') : données employé + manager comme personne à rencontrer +
     * lien FK_Employee_Origin pour retrouver ce visiteur aux prochains scans.
     */
    protected applyEmployeeAsVisitor(employee: Employee): void {
        const visit = this.editingEntity();
        if (!visit || !employee?.id) return;

        const employeeLookup = this.rawEmployeesLookup().find(e => e.id === String(employee.id));

        visit.fK_Visitor = 0;
        visit.visitor = new Visitor();
        visit.visitor.currentLanguage = this.translationService?.currentLanguageId;
        visit.visitor.fullname = employeeLookup?.value
            ?? `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim();
        visit.visitor.email = employee.email ?? undefined;
        visit.visitor.phoneNumber = employee.phoneNumber ?? undefined;
        visit.visitor.selectedPK_Visitor = [`emp_${employee.id}`];

        visit.fK_Employee = employee.fK_Manager ?? 0;
        visit.selectedPK_Employee = employee.fK_Manager ? [String(employee.fK_Manager)] : [];
        visit.fK_Employee_Origin = employee.id;

        this.editingEntity.set(Object.assign(new Visit(), visit));
    }

    override async onVisitorSelectChange(ids: string[]): Promise<void> {
        const id = ids[0];
        if (id?.startsWith('emp_')) {
            const empId = Number(id.replace('emp_', ''));
            const employee = await this.employeeDataService.get(empId).catch(() => null);
            if (employee) {
                await this.applyScannedEmployee(employee);
                return;
            }
        }
        await super.onVisitorSelectChange(ids);
    }

    override getEmptyEntity(): Visit {
        const visit = super.getEmptyEntity();
        visit.visitorType = 3; // Employee
        return visit;
    }

    protected override supportsEngagement = true;
}
